// Mitarbeiterprofil — allgemeine Angaben zu einer Person im Team.
//
// Was hier steht, hat der Server schon gefiltert: Kollegen sehen Kontakt und
// Position, die Person selbst zusätzlich ihre privaten Angaben, Administratoren
// alles samt interner Notiz. Bearbeiten darf, wer laut Server darf
// (`bearbeitbar`) — die Oberfläche fragt nicht selbst nach Rollen.
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useDaten, datum } from '../../../gemeinsam/lib/daten';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import type { Aufgabe, Anwesenheit, Benutzer, MitarbeiterProfil as Profil, ProfilFeld, Rolle } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Antwort = { benutzer: Benutzer; profil: Profil; bearbeitbar: ProfilFeld[] };
type T = (s: Schluessel) => string;

const fuellen = (text: string, werte: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (_, k: string) => String(werte[k] ?? ''));
const kuerzel = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const telLink = (n: string) => `tel:${n.replace(/[^\d+]/g, '')}`;

/** Volle Jahre zwischen zwei Daten (YYYY-MM-DD). */
function jahreSeit(von: string, bis = new Date()) {
  const d = new Date(`${von}T12:00:00`);
  let j = bis.getFullYear() - d.getFullYear();
  if (bis.getMonth() < d.getMonth() || (bis.getMonth() === d.getMonth() && bis.getDate() < d.getDate())) j -= 1;
  return Math.max(0, j);
}

export default function MitarbeiterProfil() {
  const { id = '' } = useParams();
  const { t, sprache } = useSprache();
  const { benutzer: ich, istAdmin } = useAuth();
  const { daten, laedt, fehler, neuLaden } = useDaten<Antwort>(`/users/${id}/profil`, [id]);
  const [online, setOnline] = useState<boolean | null>(null);
  const [aufgaben, setAufgaben] = useState<Aufgabe[] | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [meldung, setMeldung] = useState<{ art: 'erfolg' | 'fehler'; text: string } | null>(null);

  useEffect(() => {
    api<Anwesenheit[]>('/presence/users').then(l => setOnline(!!l.find(p => p.id === id)?.online)).catch(() => setOnline(null));
    api<Aufgabe[]>('/tasks').then(l => setAufgaben(l.filter(a => a.assigneeId === id && a.column !== 'done'))).catch(() => setAufgaben([]));
  }, [id]);

  if (laedt && !daten) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (fehler || !daten) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Link to="/team" className="text-sm text-marke-700 hover:underline dark:text-marke-300">← {t('team.alle')}</Link>
        <Hinweis art="fehler">{fehler || t('team.nichtGefunden')}</Hinweis>
      </div>
    );
  }

  const { benutzer: b, profil: p } = daten;
  const istIch = b.id === ich?.id;
  const darfBearbeiten = daten.bearbeitbar.length > 0;
  const siehtPrivat = 'geburtstag' in p;
  const siehtIntern = 'notiz' in p;
  const unterzeile = [p.position, p.abteilung].filter(Boolean).join(' · ');
  const tag = (d?: string) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(sprache, { day: 'numeric', month: 'long', year: 'numeric' }) : '');

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Link to="/team" className="text-sm text-marke-700 hover:underline dark:text-marke-300">← {t('team.alle')}</Link>
      {meldung && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}

      <Karte klasse="p-5">
        <div className="flex flex-wrap items-center gap-4">
          <span className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-marke-600 text-xl font-semibold text-white">
            {kuerzel(b.name)}
            {online !== null && (
              <span className={`absolute bottom-0.5 end-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-white dark:ring-slate-900 ${online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                    title={t(online ? 'team.online' : 'team.offline')} />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {b.name}
              {istIch && <span className="ms-2 text-sm font-normal text-slate-500 dark:text-slate-400">({t('team.sie')})</span>}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">{unterzeile || b.username}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {b.isOwner ? <Abzeichen art="blau">{t('allg.rolle.inhaber')}</Abzeichen>
                : b.role === 'admin' ? <Abzeichen art="gruen">{t('allg.rolle.admin')}</Abzeichen>
                : <Abzeichen>{t('allg.rolle.mitarbeiter')}</Abzeichen>}
              {b.status === 'frozen' && <Abzeichen art="rot">{t('team.gesperrt')}</Abzeichen>}
              {online !== null && <Abzeichen art={online ? 'gruen' : 'neutral'}>{t(online ? 'team.online' : 'team.offline')}</Abzeichen>}
            </div>
          </div>
          {darfBearbeiten && !bearbeiten && (
            <div className="w-full sm:w-auto">
              <Knopf className="w-full sm:w-auto" onClick={() => { setBearbeiten(true); setMeldung(null); }}>{t('team.bearbeiten')}</Knopf>
            </div>
          )}
        </div>
      </Karte>

      {bearbeiten ? (
        <Formular antwort={daten} t={t} istAdmin={istAdmin} istIch={istIch}
                  beiAbbruch={() => setBearbeiten(false)}
                  beiFertig={async () => { setBearbeiten(false); setMeldung({ art: 'erfolg', text: t('team.gespeichert') }); await neuLaden(); }} />
      ) : (
        <div className="grid items-start gap-4 md:grid-cols-2">
          <Karte>
            <KartenKopf titel={t('team.kontakt')} />
            <dl className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
              <Zeile titel={t('feld.email')}>{b.email && <a href={`mailto:${b.email}`} className="text-marke-700 hover:underline dark:text-marke-300">{b.email}</a>}</Zeile>
              <Zeile titel={t('feld.telefon')}>{p.telefon && <a href={telLink(p.telefon)} className="text-marke-700 hover:underline dark:text-marke-300">{p.telefon}</a>}</Zeile>
              <Zeile titel={t('team.mobil')}>{p.mobil && <a href={telLink(p.mobil)} className="text-marke-700 hover:underline dark:text-marke-300">{p.mobil}</a>}</Zeile>
              <Zeile titel={t('team.arbeitszeit')}>{p.arbeitszeit}</Zeile>
              <Zeile titel={t('team.eintritt')}>
                {p.eintritt && <>{tag(p.eintritt)} <span className="text-slate-500 dark:text-slate-400">· {(n => n === 0 ? t('team.ersteJahr') : n === 1 ? t('team.einJahr') : fuellen(t('team.jahre'), { n }))(jahreSeit(p.eintritt))}</span></>}
              </Zeile>
              <Zeile titel={t('team.benutzername')}>{b.username}</Zeile>
            </dl>
          </Karte>

          {siehtPrivat && (
            <Karte>
              <KartenKopf titel={t('team.privat')} hinweis={t('team.privatHinweis')} />
              <dl className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
                <Zeile titel={t('team.geburtstag')}>
                  {p.geburtstag && <>{tag(p.geburtstag)} <span className="text-slate-500 dark:text-slate-400">· {fuellen(t('team.alter'), { n: jahreSeit(p.geburtstag) })}</span></>}
                </Zeile>
                <Zeile titel={t('team.anschrift')}>{[p.strasse, [p.plz, p.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</Zeile>
                <Zeile titel={t('team.notfall')}>
                  {(p.notfallName || p.notfallTelefon) && <>
                    {p.notfallName}
                    {p.notfallTelefon && <> · <a href={telLink(p.notfallTelefon)} className="text-marke-700 hover:underline dark:text-marke-300">{p.notfallTelefon}</a></>}
                  </>}
                </Zeile>
              </dl>
            </Karte>
          )}

          {siehtIntern && (
            <Karte>
              <KartenKopf titel={t('team.notiz')} hinweis={t('team.notizHinweis')} />
              <p className="whitespace-pre-line px-5 py-4 text-sm text-slate-700 dark:text-slate-300">
                {p.notiz || <span className="text-slate-400">{t('team.keineAngaben')}</span>}
              </p>
            </Karte>
          )}

          <Karte>
            <KartenKopf titel={t('team.offeneAufgaben')}
                        aktion={<Link to="/aufgaben?ansicht=alle" className="text-sm text-marke-700 hover:underline dark:text-marke-300">{t('nav.aufgaben')} →</Link>} />
            {aufgaben === null ? <div className="flex justify-center py-6"><Spinner klein /></div>
              : !aufgaben.length ? <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">{t('team.keineAufgaben')}</p>
              : (
                <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  {aufgaben.slice(0, 8).map(a => (
                    <li key={a.id} className="flex items-center gap-3 px-5 py-2.5">
                      <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">{a.title}</span>
                      {a.priority === 'high' && <Abzeichen art="rot">{t('aufgaben.hoch')}</Abzeichen>}
                      {a.dueDate && <span className="text-xs text-slate-500 dark:text-slate-400">{datum(a.dueDate)}</span>}
                    </li>
                  ))}
                </ul>
              )}
          </Karte>
        </div>
      )}

      {p.aktualisiertAm && !bearbeiten && (
        <p className="px-1 text-xs text-slate-400">{fuellen(t('team.aktualisiert'), { datum: datum(p.aktualisiertAm) })}</p>
      )}
    </div>
  );
}

function Zeile({ titel, children }: { titel: string; children?: React.ReactNode }) {
  const leer = children === undefined || children === null || children === '' || children === false;
  return (
    <div className="flex gap-4 px-5 py-2.5">
      <dt className="w-32 shrink-0 text-slate-500 dark:text-slate-400">{titel}</dt>
      <dd className="min-w-0 flex-1 break-words text-slate-900 dark:text-slate-100">{leer ? <span className="text-slate-400">—</span> : children}</dd>
    </div>
  );
}

function Formular({ antwort, t, istAdmin, istIch, beiAbbruch, beiFertig }: {
  antwort: Antwort; t: T; istAdmin: boolean; istIch: boolean; beiAbbruch: () => void; beiFertig: () => Promise<void>;
}) {
  const { benutzer: b, profil: start, bearbeitbar } = antwort;
  const [p, setP] = useState<Profil>(start);
  const [konto, setKonto] = useState({ name: b.name, email: b.email || '', role: (b.role === 'admin' ? 'admin' : 'employee') as Rolle });
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const darf = (f: ProfilFeld) => bearbeitbar.includes(f);
  const kontoAenderbar = istAdmin || istIch;
  const rolleAenderbar = istAdmin && !istIch && !b.isOwner;

  const feld = (f: ProfilFeld, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <Feld label={label} value={p[f] ?? ''} disabled={!darf(f)} {...extra}
          onChange={e => setP(x => ({ ...x, [f]: e.target.value }))} />
  );

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null); setSendet(true);
    try {
      const geaendert = Object.fromEntries(bearbeitbar.filter(f => (p[f] ?? '') !== (start[f] ?? '')).map(f => [f, p[f] ?? '']));
      if (Object.keys(geaendert).length) await api(`/users/${b.id}/profil`, { methode: 'PUT', daten: geaendert });
      const kontoNeu: Record<string, string> = {};
      if (kontoAenderbar && konto.name.trim() && konto.name.trim() !== b.name) kontoNeu.name = konto.name.trim();
      if (kontoAenderbar && konto.email.trim() !== (b.email || '')) kontoNeu.email = konto.email.trim();
      if (rolleAenderbar && konto.role !== (b.role === 'admin' ? 'admin' : 'employee')) kontoNeu.role = konto.role;
      if (Object.keys(kontoNeu).length) await api(`/users/${b.id}`, { methode: 'PUT', daten: kontoNeu });
      await beiFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Speichern fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <form onSubmit={speichern} className="space-y-4">
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

      <Karte>
        <KartenKopf titel={t('team.kontakt')} hinweis={!istAdmin ? t('team.nurAdminFelder') : undefined} />
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Feld label={t('feld.name')} value={konto.name} required disabled={!kontoAenderbar}
                onChange={e => setKonto(k => ({ ...k, name: e.target.value }))} />
          <Feld label={t('feld.email')} type="email" value={konto.email} disabled={!kontoAenderbar}
                onChange={e => setKonto(k => ({ ...k, email: e.target.value }))} />
          {feld('position', t('team.position'), { placeholder: t('team.positionBeispiel') })}
          {feld('abteilung', t('team.abteilung'))}
          {feld('telefon', t('feld.telefon'), { type: 'tel' })}
          {feld('mobil', t('team.mobil'), { type: 'tel' })}
          {feld('arbeitszeit', t('team.arbeitszeit'), { placeholder: t('team.arbeitszeitBeispiel') })}
          {feld('eintritt', t('team.eintritt'), { type: 'date' })}
          {rolleAenderbar && (
            <Auswahl label={t('team.rolle')} value={konto.role} onChange={e => setKonto(k => ({ ...k, role: e.target.value as Rolle }))}>
              <option value="employee">{t('allg.rolle.mitarbeiter')}</option>
              <option value="admin">{t('allg.rolle.admin')}</option>
            </Auswahl>
          )}
        </div>
      </Karte>

      {'geburtstag' in start && (
        <Karte>
          <KartenKopf titel={t('team.privat')} hinweis={t('team.freiwillig')} />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            {feld('geburtstag', t('team.geburtstag'), { type: 'date', max: new Date().toISOString().slice(0, 10) })}
            {feld('strasse', t('feld.strasse'))}
            <div className="grid grid-cols-[100px_1fr] gap-3">
              {feld('plz', t('feld.plz'))}
              {feld('ort', t('feld.ort'))}
            </div>
            <div />
            {feld('notfallName', t('team.notfallName'), { placeholder: t('team.notfallBeispiel') })}
            {feld('notfallTelefon', t('team.notfallTelefon'), { type: 'tel' })}
          </div>
        </Karte>
      )}

      {'notiz' in start && (
        <Karte>
          <KartenKopf titel={t('team.notiz')} hinweis={t('team.notizHinweis')} />
          <div className="p-5">
            <textarea value={p.notiz ?? ''} onChange={e => setP(x => ({ ...x, notiz: e.target.value }))} rows={3} maxLength={2000}
                      aria-label={t('team.notiz')}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-marke-500 focus:outline-none focus:ring-1 focus:ring-marke-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
          </div>
        </Karte>
      )}

      <div className="flex gap-2">
        <Knopf type="submit" variante="primaer" laedt={sendet}>{t('aktion.speichern')}</Knopf>
        <Knopf type="button" onClick={beiAbbruch}>{t('aktion.abbrechen')}</Knopf>
      </div>
    </form>
  );
}
